import React, { Component } from 'react';
import { PropTypes } from 'prop-types';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import { GridHelper } from 'components/Grids/GridHelper';
import PagingButtons from 'components/Grids/PagingButtons';
import BatchFormsColDefFactory from './BatchFormsColumnDefinitions';

const AgGridReact = React.lazy(() => import(/* webpackChunkName: "ag-grid-react" */ 'ag-grid-react').then(module => ({ default: module.AgGridReact })));

export default class BatchForms extends Component {
  static propTypes = {
    settings: PropTypes.shape({}),
    permissions: PropTypes.arrayOf(PropTypes.string),
    isLoading: PropTypes.bool,
    rowData: PropTypes.arrayOf(PropTypes.shape({})),
    totalCount: PropTypes.number.isRequired,
    changePageCallback: PropTypes.func.isRequired,
    editFormAssociationsCallback: PropTypes.func.isRequired,
    getOriginalPdfFileCallback: PropTypes.func.isRequired,
    deleteFormCallback: PropTypes.func.isRequired,
    batchItem: PropTypes.shape({})
  };

  static defaultProps = {
    settings: null,
    permissions: null,
    isLoading: false,
    rowData: [],
    batchItem: null
  };

  static isSelectionEnabled = false;

  constructor(props) {
    super(props);
    this.state = {
      pageNumber: 1,
      pageSize: 50,
      sortBy: '',
      sortDirection: '',
      gridApi: null,
      filters: null,
      settingFiltersOnReload: false,
      showStatusChip: true
    };
    this.gridOptions = GridHelper.buildGridOptions({
      suppressRowClickSelection: true,
      pagination: false,
      rowSelection: 'multiple'
    });
    this.changePageCallback = this.changePageCallback.bind(this);
    this.clearReloadFlag = this.clearReloadFlag.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const {
      isLoading,
      rowData,
      totalCount,
      permissions
    } = this.props;
    const {
      gridApi,
      pageNumber,
      pageSize
    } = this.state;
    return !(
      isLoading === nextProps.isLoading
      && rowData === nextProps.rowData
      && totalCount === nextProps.totalCount
      && permissions === nextProps.permissions
      && gridApi === nextState.gridApi
      && pageNumber === nextState.pageNumber
      && pageSize === nextState.pageSize
    );
  }

  /* eslint-disable-next-line camelcase */
  UNSAFE_componentWillUpdate({
    isLoading,
    rowData,
  }, { gridApi }) {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        gridApi.setRowData(rowData);
      }
    }
  }

  componentDidUpdate(
    prevProps,
    prevState
  ) {
    const { permissions } = this.props;
    const {
      gridApi,
      sortBy,
      sortDirection
    } = this.state;

    if (this.gridOptions && sortBy && sortDirection) {
      const { columnApi } = this.gridOptions;
      if (columnApi && columnApi.getColumn(sortBy)) {
        columnApi.getColumn(sortBy).setSort(sortDirection);
      }
    }
    if (prevProps.permissions !== permissions && gridApi) {
      const params = {
        force: true
      };
      gridApi.refreshCells(params);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setGridSizeToFit.bind(this));
  }

  setGridSizeToFit() {
    const { gridApi } = this.state;

    if (gridApi) {
      GridHelper.sizeColumnsToFit(gridApi);
      GridHelper.resetRowHeights(gridApi);
    }
  }

  /* eslint-disable react/no-unused-class-component-methods */
  setSelectionVisibility(isVisible) {
    const { gridApi } = this.state;

    if (gridApi) {
      this.gridOptions.columnApi.setColumnVisible('checkbox', isVisible);
      if (!isVisible) {
        gridApi.deselectAll();
      }
      this.setGridSizeToFit();
    }
    this.isSelectionEnabled = isVisible;
  }

  getSortModel() {
    const { gridApi } = this.state;
    return gridApi ? this.gridOptions.columnApi.getColumnState().find(s => s.sort != null) : null;
  }

  getFilterModel() {
    const { gridApi } = this.state;
    return gridApi ? gridApi.getFilterModel() : null;
  }

  changePageCallback = (paging) => {
    const { changePageCallback } = this.props;

    if (paging && changePageCallback && changePageCallback(paging)) {
      this.setState({ pageNumber: paging.number, pageSize: paging.size });
      return true;
    }
    return false;
  };

  clearReloadFlag = () => {
    clearInterval(this.reloadTimer);
    this.reloadTimer = null;
    this.setState({ settingFiltersOnReload: false });
  };

  gridDidMount(gridApi) {
    window.addEventListener('resize', this.setGridSizeToFit.bind(this));
    this.setState({ gridApi });
  }

  resetPageNumber(pageNumber) {
    if (pageNumber) {
      this.setState({ pageNumber });
      this
        .pagingButtons
        .resetPageNumber(pageNumber);
    }
  }
  /* eslint-enable react/no-unused-class-component-methods */

  resizeRowHeights() {
    const { gridApi } = this.state;

    if (gridApi) {
      GridHelper.resetRowHeights(gridApi);
    }
  }

  checkOverlay() {
    const { gridApi } = this.state;

    if (gridApi && gridApi.getModel()) {
      if (gridApi.getModel().getRowCount()) {
        gridApi.hideOverlay();
      } else {
        gridApi.showNoRowsOverlay();
      }
    }
  }

  gridReloaded() {
    const {
      gridApi,
      filters
    } = this.state;

    if (filters) {
      this.setState({ settingFiltersOnReload: true });
      gridApi.setFilterModel(filters);
    }
    if (!this.reloadTimer) {
      this.reloadTimer = setInterval(this.clearReloadFlag, 2000);
    }
    this.checkOverlay();
    setTimeout(() => this.setGridSizeToFit(), 500);
  }

  filterApplied() {
    const elem = document.querySelector('.ag-menu');
    if (elem) {
      elem.style.display = 'none';
    }
    this.reloadGrid();
  }

  reloadGrid() {
    const { changePageCallback } = this.props;
    const { gridApi,
      pageSize, settingFiltersOnReload,
      sortBy, sortDirection, filters } = this.state;

    if (settingFiltersOnReload) {
      return;
    }

    if (gridApi) {
      const sortModel = this.getSortModel();
      const filterModel = this.getFilterModel();
      let sortOrFilterChanged = false;

      if (
        sortModel
        && sortModel[0]
        && (
          sortModel[0].colId !== sortBy
          || sortModel[0].sort !== sortDirection)
      ) {
        this.setState({
          pageNumber: 1,
          sortBy: sortModel[0].colId,
          sortDirection: sortModel[0].sort
        });
        sortOrFilterChanged = true;
      } else if (sortBy !== '') {
        this.setState({
          sortBy: '',
          sortDirection: ''
        });
        sortOrFilterChanged = true;
      }

      if (
        filterModel
        && Object.keys(filterModel).length > 0
        && JSON.stringify(filterModel) !== JSON.stringify(filters)
      ) {
        this.setState({
          pageNumber: 1,
          filters: filterModel
        });
        sortOrFilterChanged = true;
      } else if (
        (
          filterModel
          && Object.keys(filterModel).length === 0
        )
        && (
          filters
          && Object.keys(filters).length !== 0
        )
      ) {
        this.setState({
          filters: filterModel
        });
        sortOrFilterChanged = true;
      }
      if (sortOrFilterChanged) {
        changePageCallback({ number: 1, size: pageSize });
      }
    }
  }

  render() {
    const {
      settings,
      permissions,
      totalCount,
      getOriginalPdfFileCallback,
      deleteFormCallback,
      editFormAssociationsCallback,
      batchItem
    } = this.props;
    const {
      pageSize,
      pageNumber,
      showStatusChip,
    } = this.state;
    return (
      <div className="ag-theme-alpine ag-custom">
        <React.Suspense fallback={ <LoadingWrapper loaded={ false } /> }>
          <AgGridReact
            gridOptions={ this.gridOptions }
            columnDefs={ BatchFormsColDefFactory.createColDefs() }
            onGridReady={ params => this.gridDidMount(params.api) }
            onGridResize={ params => this.setGridSizeToFit(params.api) }
            onRowDataChanged={ () => this.gridReloaded() }
            onSortChanged={ () => this.reloadGrid() }
            onFilterChanged={ () => this.filterApplied() }
            context={ { settings, permissions, getOriginalPdfFileCallback, deleteFormCallback, editFormAssociationsCallback, batchItem, showStatusChip } }
            getRowHeight={ params => GridHelper.calculateRowHeight(params.node) }
            onVirtualColumnsChanged={ () => this.resizeRowHeights() }
            onVirtualRowRemoved={ () => this.resizeRowHeights() }
            rowBuffer={ pageSize }
          />
        </React.Suspense>
        <PagingButtons
          pageNumber={ pageNumber }
          pageSize={ pageSize }
          totalItems={ totalCount }
          changePageCallback={ this.changePageCallback }
        />
      </div>
    );
  }
}
